/**
 * DirectoryFollowButton — "Follow this business" CTA + modal capture.
 * Creates a lead in hp_directory_followers. If the biz has an email in the
 * directory doc AND is unclaimed, the server auto-fires a Resend invite to
 * the owner ("You have a follower — claim your listing to see their
 * contact details"). The customer's follow is idempotent per (email,slug).
 */
import { useState } from "react";
import { Heart, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

export default function DirectoryFollowButton({ slug, bizName }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${slug}/follow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, consent_marketing: true }),
      });
      const j = await r.json();
      if (!r.ok) { setError(j.detail || "Could not follow."); setBusy(false); return; }
      setDone(true);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const close = () => { setOpen(false); setTimeout(() => { setDone(false); setEmail(""); setName(""); setError(""); }, 250); };

  return (
    <>
      <button
        data-testid="follow-btn"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[14px] font-semibold hover:bg-rose-100"
      >
        <Heart className="w-4 h-4" /> Follow this business
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          data-testid="follow-modal"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              data-testid="follow-close"
              onClick={close}
              className="absolute top-3 right-3 p-1.5 text-stone-400 hover:text-stone-700"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            {!done ? (
              <>
                <h3 className="font-serif text-xl text-stone-900 mb-1">Follow {bizName}</h3>
                <p className="text-[13px] text-stone-500 mb-4">
                  Get notified about specials, seasonal menus and news. Unsubscribe anytime.
                </p>
                <label className="block mb-3">
                  <div className="text-[12px] font-medium text-stone-500 mb-1">Your name (optional)</div>
                  <input
                    data-testid="follow-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Sam Kim"
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:border-stone-500 text-[14px]"
                  />
                </label>
                <label className="block mb-4">
                  <div className="text-[12px] font-medium text-stone-500 mb-1">Email <span className="text-rose-600">*</span></div>
                  <input
                    data-testid="follow-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="sam@example.com"
                    required
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-200 focus:outline-none focus:border-stone-500 text-[14px]"
                  />
                </label>
                {error && <div className="text-[12px] text-rose-600 mb-2" data-testid="follow-error">{error}</div>}
                <button
                  data-testid="follow-submit"
                  onClick={submit}
                  disabled={busy || !email}
                  className="w-full px-4 py-2.5 rounded-lg bg-stone-900 text-white text-[14px] font-semibold hover:bg-stone-800 disabled:opacity-50"
                >
                  {busy ? "Following…" : "Follow"}
                </button>
                <p className="text-[11px] text-stone-400 mt-3 leading-snug">
                  By following, you agree to receive updates about this business.
                  When the owner claims their listing, they will be able to reach you.
                </p>
              </>
            ) : (
              <div className="text-center py-4" data-testid="follow-thanks">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-6 h-6 text-emerald-600 fill-emerald-600" />
                </div>
                <h3 className="font-serif text-lg text-stone-900 mb-1">You are following {bizName}</h3>
                <p className="text-[13px] text-stone-500 mb-4">
                  We will keep you posted. Want to see more listings like this?
                </p>
                <a
                  href="/directory"
                  className="inline-block px-4 py-2 rounded-lg bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-800"
                >
                  Browse HustleDirectory →
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
